import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, Skeleton, cn, cssVars } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import type { AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useTopics } from "./use-analytics";

const UNCATEGORIZED = "uncategorized";
const FLAT_TREND_PCT = 2;

function TrendBadge({ pct }: { pct: number | null }) {
  const { t } = useTranslation();
  if (pct === null) return <span className="inline-flex w-16 justify-end text-xs font-bold text-fg-subtle">{t("engage.analytics.topics.newTopic")}</span>;
  const value = `${Math.abs(Math.round(pct)).toString()}%`;
  const flat = Math.abs(pct) < FLAT_TREND_PCT;
  const label = flat ? t("engage.analytics.topics.trendFlat") : pct > 0 ? t("engage.analytics.topics.trendUp", { value }) : t("engage.analytics.topics.trendDown", { value });
  return (
    <span className={cn("inline-flex w-16 items-center justify-end gap-0.5 text-xs font-bold", flat ? "text-fg-subtle" : pct > 0 ? "text-success" : "text-danger")} aria-label={label} role="img">
      <Icon name={flat ? "arrow-right" : "arrow-up"} className={cn("size-3", !flat && pct < 0 && "rotate-180")} />
      {value}
    </span>
  );
}

function ShareBar({ share, label }: { share: number; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div aria-hidden="true" className="h-2 flex-1 rounded-pill bg-sunken">
        <div className="h-2 w-[var(--geo-width)] rounded-pill bg-brand-500" style={cssVars({ "--geo-width": `${(share * 100).toFixed(1)}%` })} />
      </div>
      <span className="w-10 text-right text-xs tabular-nums text-fg-muted">{label}</span>
    </div>
  );
}

/** Topic clusters (`GET /v1/analytics/topics`): count + trend vs previous period. */
export function TopicsCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { summary } = useWorkspaceContext();
  const query = useTopics(filters);
  const topicLabel = (topic: string) => (topic === UNCATEGORIZED ? t("engage.analytics.topics.uncategorized") : topic);
  return (
    <ChartCard
      flush
      className="xl:col-span-2"
      testId="analytics-topics"
      title={t("engage.analytics.topics.title")}
      aside={
        <AppLink href={consoleRoutes.gaps(summary.slug)} className="text-xs font-semibold text-primary-ink hover:underline">
          {t("engage.analytics.topics.seeGaps")}
        </AppLink>
      }
      query={query}
      isEmpty={(data) => data.items.length === 0}
      empty={{ icon: "layers", title: t("engage.analytics.topics.emptyTitle"), body: t("engage.analytics.topics.emptyBody") }}
      skeleton={
        <ul className="divide-y divide-line">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="flex items-center gap-3 px-5 py-3">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-12" />
            </li>
          ))}
        </ul>
      }
    >
      {(data) => {
        const total = data.items.reduce((sum, item) => sum + item.count, 0);
        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable)
          <div className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-primary" role="region" aria-label={t("engage.analytics.topics.title")} tabIndex={0}>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("engage.analytics.topics.colTopic")}</th>
                  <th scope="col" className="text-right">
                    {t("engage.analytics.topics.colConversations")}
                  </th>
                  <th scope="col" className="w-40">
                    {t("engage.analytics.topics.colShare")}
                  </th>
                  <th scope="col" className="text-right">
                    {t("engage.analytics.topics.vsPrevious")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.topic}>
                    <td className="font-semibold">{topicLabel(item.topic)}</td>
                    <td className="text-right tabular-nums">{fmt.number(item.count)}</td>
                    <td>
                      <ShareBar share={total > 0 ? item.count / total : 0} label={fmt.percent(total > 0 ? item.count / total : 0)} />
                    </td>
                    <td className="text-right">
                      <TrendBadge pct={item.trend_pct} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }}
    </ChartCard>
  );
}
